import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthCard } from "../components/auth/AuthCard";
import { PasswordInput } from "../components/auth/PasswordInput";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "../hooks/usePageTitle";
import { Loader2, AlertCircle } from "lucide-react";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

function LoginPage() {
  usePageTitle("Log in");
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const planParam = (params.get("plan") || localStorage.getItem("theo_selected_plan") || "solo").toLowerCase();
  const selectedPlan = ["solo", "practice", "firm", "enterprise"].includes(planParam) ? planParam : "solo";
  const redirectUrl = params.get("redirect");
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_expired") === "1") {
      toast({
        title: "Session expired",
        description: "Please sign in again.",
        variant: "destructive",
      });
      params.delete("session_expired");
      const newUrl = params.toString() 
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const res = await fetch(`${backendUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      
      // Handle non-OK responses (401, etc) with friendly message
      if (!res.ok) {
        setError("Incorrect email or password. Please try again or reset your password.");
        return;
      }
      
      const data = await res.json();
      
      localStorage.setItem("theo_token", data.access_token);
      localStorage.setItem("theo_selected_plan", selectedPlan);
      
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        window.location.href = "/app/today";
      }
    } catch (err) {
      // Network errors - show friendly message
      setError("Incorrect email or password. Please try again or reset your password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      headline={<>Welcome back to <span style={{ color: "#19B6A2" }}>Theo</span></>}
      supporting="Sign in to access your workspace and keep your client relationships organized."
      footer={
        <p className="text-center" style={{ fontSize: "13px", color: "#64748B" }}>
          New to Theo?{" "}
          <Link
            to="/register"
            data-testid="login-register-link"
            style={{ color: "#19B6A2" }}
            className="font-medium hover:underline"
          >
            Create workspace
          </Link>
        </p>
      }
    >
      <form data-testid="login-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Inline Error Message */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Work Email */}
        <div style={{ marginBottom: "16px" }}>
          <label
            htmlFor="email"
            className="block text-[13px] font-medium mb-1.5 text-slate-600"
          >
            Email
          </label>
          <Input
            id="email"
            data-testid="login-email-input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(""); }}
            className="focus-visible:ring-2 focus-visible:ring-offset-0"
          />
        </div>

        {/* Password */}
        <div style={{ marginBottom: "8px" }}>
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor="password"
              className="block text-[13px] font-medium text-slate-600"
            >
              Password
            </label>
            <Link
              to="/forgot-password"
              className="font-medium hover:underline"
              style={{ fontSize: "12px", color: "#19B6A2" }}
              data-testid="login-forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            id="password"
            data-testid="login-password-input"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(""); }}
            className="focus-visible:ring-2 focus-visible:ring-offset-0"
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          data-testid="login-submit-button"
          disabled={loading}
          className="w-full h-11 rounded-[9px] font-semibold tracking-wide transition-colors mt-6 bg-[#19B6A2] text-white hover:bg-[#139685] disabled:bg-slate-400 text-[14px]"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in…
            </>
          ) : (
            "Sign in"
          )}
        </Button>
      </form>
    </AuthCard>
  );
}

export default LoginPage;
